import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useCallback, useState } from 'react';
import {
  useRemoveServiceMutation,
  useSaveServiceMutation,
  useServicesQuery,
  useServiceTypesQuery,
} from '@/queries/services';
import Portal from '../../modal/Portal';
import { SectionNavbar } from '../../section-navbar';
import { AssignedSponsors } from '../../sponsors';
import { ServiceForm } from '../../services';
import {
  EventRelatedItemRow,
  EventRelatedSection,
} from '../EventRelatedSection';

export function ServicesSection({ festivalId }) {
  const servicesQ = useServicesQuery(festivalId);
  const typesQ = useServiceTypesQuery();
  const saveMutation = useSaveServiceMutation(festivalId);
  const removeMutation = useRemoveServiceMutation(festivalId);

  const services = servicesQ.data ?? [];
  const serviceTypes = typesQ.data ?? {};
  const loading =
    festivalId == null ||
    servicesQ.isPending ||
    servicesQ.isFetching ||
    typesQ.isPending ||
    typesQ.isFetching;

  const [showModal, setShowModal] = useState(false);
  const [selectedService, setSelectedService] = useState({});
  const [pendingDelete, setPendingDelete] = useState(null);

  const openModal = (service) => {
    const next =
      service?.serviceId != null
        ? services.find((s) => s.serviceId === service.serviceId) || {}
        : {};
    setSelectedService(next);
    setShowModal(true);
  };

  const onFileUpload = (fileInfo) => {
    const { filename, url } = fileInfo;
    setSelectedService((prev) => ({
      ...prev,
      photoURL: url,
      servicePhoto: filename,
    }));
  };

  const onFileDelete = () => {
    setSelectedService((prev) => ({
      ...prev,
      photoURL: null,
      servicePhoto: null,
    }));
  };

  const handleLocationChange = (location) => {
    setSelectedService((prev) => ({
      ...prev,
      location: {
        ...prev.location,
        ...location,
        radius: 50,
      },
    }));
  };

  const handleLocationNameChange = (locationName) => {
    setSelectedService((prev) => ({
      ...prev,
      location: {
        ...prev.location,
        locationName,
      },
    }));
  };

  const handleSave = async (service) => {
    const updatedService = { ...selectedService, ...service };
    try {
      await saveMutation.mutateAsync(updatedService);
      setShowModal(false);
    } catch {
      /* mutation toast */
    }
  };

  const requestDelete = useCallback((service) => {
    if (!service?.serviceId) {
      return;
    }
    setPendingDelete(service);
  }, []);

  const cancelDelete = useCallback(() => {
    setPendingDelete(null);
  }, []);

  const confirmDelete = useCallback(async () => {
    const service = pendingDelete;
    if (!service?.serviceId) {
      return;
    }
    try {
      await removeMutation.mutateAsync(service.serviceId);
      setPendingDelete(null);
      if (selectedService.serviceId === service.serviceId) {
        setShowModal(false);
        setSelectedService({});
      }
    } catch {
      /* mutation toast */
    }
  }, [pendingDelete, removeMutation, selectedService.serviceId]);

  const serviceForm = (
    <ServiceForm
      types={serviceTypes}
      service={selectedService}
      handleSave={handleSave}
      handleLocationChange={handleLocationChange}
      handleLocationNameChange={handleLocationNameChange}
      onFileDelete={onFileDelete}
      onFileUpload={onFileUpload}
    />
  );

  const sections = [
    { title: 'Information', component: serviceForm },
    {
      title: 'Sponsors',
      component: (
        <AssignedSponsors
          objectId={selectedService.serviceId}
          objectType="service"
        />
      ),
    },
  ];

  return (
    <>
      <EventRelatedSection
        icon={['fas', 'hand-holding-heart']}
        title="Services"
        loading={loading}
        onAdd={() => openModal({})}
        addLabel="Add Service"
      >
        {services.map((service) => (
          <EventRelatedItemRow
            key={service.serviceId}
            label={service.serviceName || 'Untitled service'}
            meta={service.type}
            onEdit={() => openModal(service)}
            onDelete={() => requestDelete(service)}
          />
        ))}
      </EventRelatedSection>

      <Portal
        header={selectedService?.serviceName || 'Add New Service'}
        open={showModal}
        onClose={() => setShowModal(false)}
      >
        <SectionNavbar navItems={sections} />
      </Portal>

      <Portal
        header="Delete service"
        open={Boolean(pendingDelete)}
        onClose={cancelDelete}
        size="sm"
      >
        <div className="py-2">
          <p className="m-0 text-base leading-relaxed text-[#121212]">
            Are you sure you want to delete this service?
          </p>
          <div className="mt-6 flex flex-wrap justify-end gap-3">
            <button
              type="button"
              className="inline-flex min-h-11 min-w-[120px] items-center justify-center rounded-[var(--bethere-radius-md)] border-2 border-primary bg-transparent px-5 font-bold text-primary transition-all hover:-translate-y-px hover:bg-primary hover:text-primary-foreground"
              onClick={cancelDelete}
            >
              Cancel
            </button>
            <button
              type="button"
              className="inline-flex min-h-11 min-w-[120px] items-center justify-center gap-2 rounded-[var(--bethere-radius-md)] border border-transparent bg-[#c9302c] px-5 font-bold text-white transition-all hover:-translate-y-px hover:brightness-110 disabled:opacity-60"
              onClick={confirmDelete}
              disabled={removeMutation.isPending}
            >
              <FontAwesomeIcon icon={['fas', 'trash']} />
              Delete
            </button>
          </div>
        </div>
      </Portal>
    </>
  );
}
